'use context';
import { useState, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { createPortal } from 'react-dom';
import { type SlideImage, useLightboxState } from 'yet-another-react-lightbox';
import Image from 'components/UI/Image/Image';
import { VideoPlayerControlsLoading } from 'components/UI/Loader';
import { AdSlotName } from 'types/ads';
import { AdSlot } from '../AdSlot';
import { Icon } from '../Icon';
import styles from './Gallery.module.scss';

export const GallerySlide = ({
  slide,
  className,
  isFullscreen = false,
  fullscreenOpen = false,
}: {
  slide: SlideImage;
  className?: string;
  isFullscreen?: boolean;
  fullscreenOpen?: boolean;
}) => {
  const state = useLightboxState();
  const slideIndex = state.slides.findIndex((s) => s === slide);
  const [showFullCaption, setShowFullCaption] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const [lastCurrentIndex, setLastCurrentIndex] = useState(state.currentIndex);
  const captionRef = useRef<HTMLParagraphElement>(null);
  const portalRootId = isFullscreen ? 'portal-root-fullscreen' : 'portal-root-inline';

  const caption = (slide.fields?.caption as string) ?? slide.title ?? '';

  if (state.currentIndex !== lastCurrentIndex) {
    setShowFullCaption(false);
    setLastCurrentIndex(state.currentIndex);
  }

  const currentIndex = state.currentIndex;
  const isVisible = currentIndex - slideIndex === 0;
  const shouldHaveId =
    isVisible && slide.adslot === true && (isFullscreen ? currentIndex === slideIndex : !fullscreenOpen);

  useEffect(() => {
    if (captionRef.current) {
      const element = captionRef.current;
      setIsTruncated(element.scrollHeight > element.clientHeight);
    }
  }, [caption, state.currentIndex]);

  useEffect(() => {
    if (shouldHaveId && typeof window !== 'undefined' && window?.MoltenBundle !== undefined) {
      window.MoltenBundle.cmd.push(() => {
        window.MoltenBundle.getClient().reloadAd('biga_1');
      });
    }
  }, [shouldHaveId]);

  const captionOverlay = showFullCaption ? (
    <dialog
      className={styles['caption-overlay']}
      onClick={(e) => e.stopPropagation()}
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setShowFullCaption(false);
        }
      }}
    >
      <div className={styles['caption-overlay-content']}>
        <p className={styles['slide-caption-full']}>{caption}</p>
      </div>
      <button
        className={clsx(styles.button, styles['caption-close-button'])}
        onClick={(e) => {
          e.stopPropagation();
          setShowFullCaption(false);
        }}
        type="button"
        aria-label="Close caption"
      >
        <Icon name="close" />
      </button>
    </dialog>
  ) : null;

  if (slide.loading) {
    return <VideoPlayerControlsLoading />;
  }

  if (slide.adslot) {
    return (
      <AdSlot name={shouldHaveId ? AdSlotName.Gallery : AdSlotName.Empty} className={clsx(styles.slide, [className])} />
    );
  }

  return (
    <figure className={clsx(styles.slide, [className])}>
      <picture className={styles['slide-image-wrapper']}>
        <Image
          image={slide.image}
          alt={slide.title}
          cropOverride="original"
          maxHeight={1200}
          priority={slideIndex === 0}
        />
      </picture>
      <figcaption className={styles['slide-info']}>
        {!showFullCaption && (
          <>
            <div className={styles['caption-container']}>
              <p ref={captionRef} className={clsx(styles['slide-caption'], styles['slide-caption-truncated'])}>
                {caption}
              </p>
              {isTruncated && (
                <button
                  className={styles['caption-more-button']}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFullCaption(true);
                  }}
                  type="button"
                >
                  ...mehr
                </button>
              )}
            </div>
            <p className={styles['slide-credits']}>{(slide.fields?.copyright as string) ?? 'RTL'}</p>
          </>
        )}
      </figcaption>

      {captionOverlay && createPortal(captionOverlay, document.getElementById(portalRootId)!)}
    </figure>
  );
};
